// shared-lib/geometry/wallFace.js
'use strict';

/**
 * A wall's DRAWN face, up the wall — the frame a texture region's `y` /
 * `height` percentages are read in.
 *
 * The 3D tiles a region over the face mesh, which is built from the wall's
 * `polygonCorners`, and the elevations draw it on the same face
 * (`web-app/CLAUDE_TEXTURING.md`, "The region frame"). Where that face is not
 * the wall's stored box — a top that follows a raised or sloped ceiling — a
 * percentage of the STORED height lands somewhere else: a backsplash written
 * that way stood 0.94" above its counter, a strip of painted wall between the
 * two (Cabin, 2428 Lore Rd, 2026-10-26; 3 of the 133 dev walls carrying one).
 *
 * So every writer and reader of a region's height goes through here: the
 * outline's lowest and highest points where the wall has one — exactly what
 * the drawings read (`elevationWallFinishes.faceRange`) — else its stored box.
 *
 * Pure. No I/O.
 */

/**
 * @returns {{bottom: number, top: number, height: number}|null} world Y of
 *   the face's bottom and top, and its height; null for a wall with no face
 */
function wallFaceSpanY(wall) {
  const corners = wall?.polygonCorners;
  if (Array.isArray(corners) && corners.length >= 3) {
    let bottom = Infinity;
    let top = -Infinity;
    for (const c of corners) {
      const y = c?.y ?? 0;
      if (y < bottom) bottom = y;
      if (y > top) top = y;
    }
    if (top > bottom) return { bottom, top, height: top - bottom };
  }
  const h = wall?.dimensions?.y ?? wall?.dimensions?.height;
  if (!wall?.center || !(h > 0)) return null;
  const bottom = (wall.center.y ?? 0) - h / 2;
  return { bottom, top: bottom + h, height: h };
}

/**
 * The drawn face's height alone — the outline's span where the wall has one,
 * else its stored height. Needs no centre, unlike the span's bottom.
 *
 * @returns {number|null}
 */
function wallFaceHeight(wall) {
  const span = wallFaceSpanY(wall);
  if (span) return span.height;
  const h = wall?.dimensions?.y ?? wall?.dimensions?.height;
  return h > 0 ? h : null;
}

/** A world Y as a percentage up the wall's drawn face, or null. */
function wallYToPercent(wall, y) {
  const face = wallFaceSpanY(wall);
  return face ? ((y - face.bottom) / face.height) * 100 : null;
}

/** A percentage up the wall's drawn face as a world Y, or null. */
function wallPercentToY(wall, pct) {
  const face = wallFaceSpanY(wall);
  return face ? face.bottom + (pct / 100) * face.height : null;
}

module.exports = {
  wallFaceSpanY,
  wallFaceHeight,
  wallYToPercent,
  wallPercentToY,
};
